import { Activity, ArrowDownRight, ArrowUpRight, BarChart2, Calculator, Calendar, Coins, History, Layers3, Settings2, Sparkles, TrendingUp, Trophy } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { BUILT_IN_KPIS, normaliseVisibleKpis } from "@/components/MetricsConfigPanel";

const BUILT_IN_ICON = {
  current_mrr:           Coins,
  arr_run_rate:          Sparkles,
  arr_fy:                BarChart2,
  arr_last_fy:           History,
  arr_next_fy:           ArrowUpRight,
  arr_churned_fy:        ArrowDownRight,
  closed_won_amount_fy:  Trophy,
  closed_won_amount_cy:  Calendar,
  pipeline_value:        TrendingUp,
  active_deals:          Activity,
  products_in_view:      Layers3,
  open_ended_line_items: Calculator,
};

function evaluateCustom(metric, kpis, independentKpis = {}) {
  const getVal = (op) => {
    if (!op) return 0;
    if (op.type === "metric") return kpis?.[op.key] ?? 0;
    if (op.type === "independent_kpi") {
      const pipes = (op.pipelines || []).slice().sort().join(",") || "__all__";
      const stgs  = (op.stages   || []).slice().sort().join(",") || "__all__";
      const mode  = op.revenue_mode === "projected" ? "projected" : "actual";
      const cacheKey = `${pipes}|${stgs}|${mode}`;
      // Fall back to global kpis when the pipeline-filtered cache isn't populated yet
      return independentKpis?.[cacheKey]?.[op.key] ?? kpis?.[op.key] ?? 0;
    }
    return op.value ?? 0;
  };
  // Simple mode: just the value of operand_a, no formula needed
  if (metric.mode === "simple") return getVal(metric.operand_a);
  const a = getVal(metric.operand_a);
  const b = getVal(metric.operand_b);
  switch (metric.operator) {
    case "+": return a + b;
    case "-": return a - b;
    case "*": return a * b;
    case "/": return b !== 0 ? a / b : 0;
    default:  return 0;
  }
}

export default function KpiGrid({ kpis, formatCurrency, visibleKpis, customMetrics, fiscalYearStartMonth, onConfigure, independentKpis = {} }) {
  const allCustom    = customMetrics ?? [];
  const defaultKeys  = BUILT_IN_KPIS.map((k) => k.key);
  // Normalised unified order: built-in keys + custom metric IDs in one array.
  const orderedKeys  = normaliseVisibleKpis(visibleKpis ?? defaultKeys, allCustom);

  const totalCards = orderedKeys.length;

  // Render a single card for either a built-in key or a custom metric ID.
  function renderCard(id) {
    const builtin = BUILT_IN_KPIS.find((m) => m.key === id);
    if (builtin) {
      const { key, label, format } = builtin;
      const Icon         = BUILT_IN_ICON[key] ?? Calculator;
      const value        = kpis?.[key] ?? 0;
      const displayValue = format === "currency"
        ? formatCurrency(value)
        : Intl.NumberFormat("en-US").format(value);
      return (
        <Card
          key={key}
          className="border-[#cccccc] bg-white shadow-sm transition duration-200 hover:shadow-md"
          data-testid={`kpi-card-${key}`}
        >
          <CardContent className="flex items-start justify-between gap-4 p-5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]" data-testid={`kpi-label-${key}`}>
                {label}
              </p>
              <p className="mt-3 font-sans text-3xl font-semibold tracking-tight text-[#323c44]" data-testid={`kpi-value-${key}`}>
                {displayValue}
              </p>
            </div>
            <div className="rounded-sm bg-[#f5f5f5] p-2.5 text-[#119996]" data-testid={`kpi-icon-${key}`}>
              <Icon className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      );
    }

    const metric = allCustom.find((m) => m.id === id);
    if (!metric) return null;
    const rawValue = evaluateCustom(metric, kpis, independentKpis);

    // invert_sign: when B > A the raw result is negative; treat that as a surplus
    // and display it as "+£value" in green. When A > B (behind target) display as
    // "-£value" in red.
    let displayValue;
    let valueColor = "text-[#323c44]";
    if (metric.invert_sign) {
      const absVal = Math.abs(rawValue);
      const formatted = metric.format === "currency"
        ? formatCurrency(absVal)
        : Intl.NumberFormat("en-US").format(absVal);
      if (rawValue < 0) {
        // B exceeded A → surplus
        displayValue = `+${formatted}`;
        valueColor   = "text-[#10b981]";
      } else if (rawValue > 0) {
        // A exceeded B → behind target
        displayValue = `-${formatted}`;
        valueColor   = "text-[#ef4444]";
      } else {
        displayValue = formatted;
      }
    } else {
      displayValue = metric.format === "currency"
        ? formatCurrency(rawValue)
        : Intl.NumberFormat("en-US").format(rawValue);
    }

    return (
      <Card
        key={metric.id}
        className="border-[#cccccc] bg-white shadow-sm transition duration-200 hover:shadow-md"
        data-testid={`kpi-card-custom-${metric.id}`}
      >
        <CardContent className="flex items-start justify-between gap-4 p-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">
              {metric.name}
            </p>
            <p className={`mt-3 font-sans text-3xl font-semibold tracking-tight ${valueColor}`}>
              {displayValue}
            </p>
          </div>
          <div className="rounded-sm bg-[#f5f5f5] p-2.5 text-[#119996]">
            <Calculator className="h-5 w-5" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2" data-testid="kpi-grid">
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={onConfigure}
          className="flex items-center gap-1.5 rounded-md border border-[#dddddd] bg-white px-3 py-1.5 text-xs font-medium text-[#4a5b68] transition hover:bg-[#efefef]"
          data-testid="configure-metrics-button"
        >
          <Settings2 className="h-3.5 w-3.5" />
          Configure metrics
        </button>
      </div>

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: totalCards > 0 ? `repeat(${totalCards}, minmax(0, 1fr))` : undefined }}
      >
        {orderedKeys.map(renderCard)}
      </div>
    </div>
  );
}
